import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Archive, ArrowUpDown } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';

const StockAdjustModal = ({ productId, productName, onClose }) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    variante_id: '',
    armazem_id: '',
    quantidade: '',
    tipo: 'IN',
    lote: '',
    validade: '',
    localizacao: '',
    observacoes: '',
  });

  // Carregar produto com variantes
  const { data: productDetail, isLoading: loadingProduct } = useQuery({
    queryKey: ['inventory-product', productId],
    queryFn: () => inventoryService.getProduct(productId),
    enabled: !!productId,
  });

  // Carregar estoque atual por armazém para simulação em tempo real
  const { data: stockWarehouseData } = useQuery({
    queryKey: ['inventory-stock-by-warehouse', productId],
    queryFn: () => inventoryService.getStockByWarehouse(productId),
    enabled: !!productId,
  });

  // Fechar ao pressionar Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Carregar armazéns
  const { data: warehousesData, isLoading: loadingWarehouses } = useQuery({
    queryKey: ['inventory-warehouses'],
    queryFn: inventoryService.getWarehouses,
  });

  const adjustStockMutation = useMutation({
    mutationFn: (payload) => inventoryService.adjustStock(payload),
    onSuccess: () => {
      toast.success('Estoque ajustado com sucesso!');
      // Atualizar listas relacionadas
      queryClient.invalidateQueries(['inventory-stock']);
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      onClose?.();
    },
    onError: (error) => {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erro ao ajustar estoque';
      toast.error(msg);
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.variante_id || !formData.armazem_id || !formData.quantidade) {
      toast.error('Selecione a variante, o armazém e informe a quantidade.');
      return;
    }

    const payload = {
      produto_id: productId,
      variante_id: Number(formData.variante_id),
      armazem_id: Number(formData.armazem_id),
      quantidade: Number(formData.quantidade),
      tipo: formData.tipo,
      lote: formData.lote || null,
      validade: formData.validade || null,
      localizacao: formData.localizacao || null,
      observacoes: formData.observacoes || null,
    };

    adjustStockMutation.mutate(payload);
  };

  const variants = productDetail?.data?.variantes || [];
  const warehouses = warehousesData?.data || [];

  const selectedVariantId = Number(formData.variante_id);
  const selectedWarehouseId = Number(formData.armazem_id);

  let currentSaldo = null;
  if (selectedVariantId && selectedWarehouseId && stockWarehouseData?.data?.variantes) {
    const v = stockWarehouseData.data.variantes.find(item => item.variante_id === selectedVariantId);
    if (v && v.warehouses) {
      const wh = v.warehouses.find(w => w.armazem_id === selectedWarehouseId);
      currentSaldo = wh ? (parseFloat(wh.estoque_disponivel) || 0) : 0;
    }
  }

  const parsedQty = parseFloat(formData.quantidade) || 0;
  let projectedSaldo = null;
  if (currentSaldo !== null && !isNaN(parsedQty)) {
    if (formData.tipo === 'IN') {
      projectedSaldo = currentSaldo + Math.abs(parsedQty);
    } else if (formData.tipo === 'OUT') {
      projectedSaldo = currentSaldo - Math.abs(parsedQty);
    } else {
      projectedSaldo = currentSaldo + parsedQty;
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-primary-600" />
            <div>
              <h2 className="text-lg font-bold text-secondary-900">
                Ajustar Estoque
              </h2>
              <p className="text-xs text-secondary-500 truncate max-w-xs">
                Produto: {productName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {loadingProduct || loadingWarehouses ? (
            <div className="py-8 text-center text-secondary-500 text-sm">
              Carregando dados...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Variante *
                  </label>
                  <select
                    value={formData.variante_id}
                    onChange={(e) => handleChange('variante_id', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Selecione uma variante</option>
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.sku_variacao ||
                          v.cor ||
                          v.tamanho_label ||
                          `Variante #${v.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Armazém *
                  </label>
                  <select
                    value={formData.armazem_id}
                    onChange={(e) => handleChange('armazem_id', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Selecione um armazém</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Tipo de movimento
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="w-4 h-4 text-secondary-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <select
                      value={formData.tipo}
                      onChange={(e) => handleChange('tipo', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="IN">Entrada (IN)</option>
                      <option value="OUT">Saída (OUT)</option>
                      <option value="AJUSTE">Ajuste</option>
                    </select>
                  </div>
                </div>

                {/* Simulador de Saldo em Tempo Real */}
                {currentSaldo !== null && (
                  <div className={`md:col-span-2 p-3 rounded-lg border text-xs flex flex-wrap items-center justify-between gap-3 ${
                    projectedSaldo < 0 
                      ? 'bg-red-50 border-red-200 text-red-800' 
                      : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  }`}>
                    <div>
                      <span className="font-bold block text-[11px] uppercase tracking-wider text-secondary-500">
                        Simulação de Saldo Físico
                      </span>
                      <span className="text-secondary-700">
                        Saldo Atual Disponível: <strong className="text-secondary-900">{currentSaldo.toLocaleString('pt-BR')}</strong>
                      </span>
                    </div>

                    <div className="text-center">
                      <span className="text-[10px] text-secondary-500 block">Movimento</span>
                      <span className={`font-mono font-bold text-xs ${
                        formData.tipo === 'OUT' ? 'text-red-600' : 'text-emerald-700'
                      }`}>
                        {formData.tipo === 'OUT' 
                          ? `- ${Math.abs(parsedQty).toLocaleString('pt-BR')}` 
                          : `+ ${Math.abs(parsedQty).toLocaleString('pt-BR')}`}
                      </span>
                    </div>

                    <div className="text-right">
                      <span className="text-[10px] text-secondary-500 block">Saldo Projetado</span>
                      <span className={`font-bold text-sm font-mono ${
                        projectedSaldo < 0 ? 'text-red-600' : 'text-emerald-700'
                      }`}>
                        {projectedSaldo.toLocaleString('pt-BR')} {projectedSaldo < 0 ? '⚠️' : '✓'}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Quantidade *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantidade}
                    onChange={(e) => handleChange('quantidade', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: 10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Lote
                  </label>
                  <input
                    type="text"
                    value={formData.lote}
                    onChange={(e) => handleChange('lote', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Opcional"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Validade
                  </label>
                  <input
                    type="date"
                    value={formData.validade}
                    onChange={(e) => handleChange('validade', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Localização
                  </label>
                  <input
                    type="text"
                    value={formData.localizacao}
                    onChange={(e) => handleChange('localizacao', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: Prateleira A3"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => handleChange('observacoes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Detalhes sobre o ajuste (ex: estoque inicial, correção, etc.)"
                />
              </div>
            </>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-secondary-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={adjustStockMutation.isPending || adjustStockMutation.isLoading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {adjustStockMutation.isPending || adjustStockMutation.isLoading ? 'Salvando...' : 'Salvar Ajuste'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustModal;


